import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import { toast } from 'react-toastify';

function Feedback() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchAppointment = async () => {
      try {
        const res = await api.get(`appointments/user/my-bookings/`);
        const appointment = res.data.find(a => a.appointment_id === parseInt(appointmentId));
        if (appointment) {
          setDoctor({
            id: appointment.doctor_id,
            name: appointment.doctor_name
          });
        } else {
          toast.error('Appointment not found');
          navigate('/user/dashboard');
        }
      } catch (err) {
        console.error('Failed to load appointment detail', err);
        toast.error('Unable to load appointment details');
      } finally {
        setLoading(false);
      }
    };
    fetchAppointment();
  }, [appointmentId, navigate]);

  const submit = async () => {
    if (!rating) {
      toast.warning('Please select a rating');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        doctor: doctor.id,
        rating,
        review_text: text,
        appointment: appointmentId
      };
      await api.post('ratings/submit/', payload);
      toast.success('Your feedback has been submitted! Thank you.');
      navigate('/user/dashboard');
    } catch (err) {
      console.error('Failed to submit feedback', err);
      const errorMsg = err.response?.data?.error || 'Failed to submit feedback';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  return (
    <div className="bg-soft min-vh-100 pb-5">
      <UserNavbar />

      <div className="container py-5 mt-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden bg-white">
              {/* Header Section */}
              <div className="p-4 text-center border-bottom bg-light bg-opacity-50">
                <div className="mb-3">
                  <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '70px', height: '70px' }}>
                    <i className="bi bi-chat-heart-fill fs-2 text-primary"></i>
                  </div>
                </div>
                <h4 className="fw-bold text-dark mb-1">Your Feedback Matters</h4>
                <p className="text-muted small mb-0">How was your session with <strong> {doctor?.name}</strong>?</p>
              </div>

              <div className="card-body p-4 p-lg-5">
                {/* Rating Section */}
                <div className="text-center mb-5">
                  <p className="text-muted fw-medium mb-3">Overall Experience</p>
                  <div className="d-flex justify-content-center gap-2 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className="btn p-0 border-0 bg-transparent transition-transform"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHover(star)}
                        onMouseLeave={() => setHover(0)}
                        style={{
                          cursor: 'pointer',
                          transform: (hover || rating) >= star ? 'scale(1.15)' : 'scale(1)'
                        }}
                      >
                        <i className={`bi ${(hover || rating) >= star ? 'bi-star-fill text-warning' : 'bi-star text-muted opacity-40'} fs-1`}></i>
                      </button>
                    ))}
                  </div>
                  <div className="badge bg-soft-primary px-3 py-2 rounded-pill mt-2">
                    <span className="text-primary fw-bold">
                      {rating === 5 ? 'Exceptional' : rating === 4 ? 'Very Good' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : rating === 1 ? 'Poor' : 'Select Rating'}
                    </span>
                  </div>
                </div>

                {/* Feedback Text Area */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label fw-bold text-dark small mb-0">Tell us more about it</label>
                    <span className={`small ${text.length > 450 ? 'text-danger' : 'text-muted'}`}>
                      {text.length}/500
                    </span>
                  </div>
                  <textarea
                    className="form-control border-soft p-3 rounded-4 bg-soft shadow-none focus-primary"
                    rows={4}
                    value={text}
                    onChange={(e) => text.length < 500 && setText(e.target.value)}
                    placeholder="Describe your consultation, the doctor's communication, or any suggestions you have..."
                    style={{ resize: 'none', transition: 'all 0.2s ease' }}
                  />
                </div>

                {/* Buttons */}
                <div className="d-flex flex-column gap-3">
                  <button
                    className="btn btn-primary btn-lg rounded-pill fw-bold py-3 shadow-hover border-0"
                    onClick={submit}
                    disabled={submitting || !rating}
                    style={{ transition: 'all 0.3s ease' }}
                  >
                    {submitting ? (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    ) : (
                      <i className="bi bi-check2-circle me-2"></i>
                    )}
                    {submitting ? 'Submitting...' : 'Submit Review'}
                  </button>
                  <button
                    className="btn btn-link text-muted text-decoration-none small fw-medium"
                    onClick={() => navigate('/user/dashboard')}
                  >
                    Skip for now
                  </button>
                </div>
              </div>
            </div>

            {/* Privacy Note */}
            <div className="text-center mt-4">
              <div className="d-inline-flex align-items-center bg-white px-3 py-2 rounded-pill shadow-sm border border-light">
                <i className="bi bi-shield-check text-success me-2"></i>
                <span className="text-muted small">Your feedback is securely shared for quality improvement.</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .bg-soft { background-color: #f8fbff; }
        .border-soft { border: 1.5px solid #eef2f7; }
        .bg-soft-primary { background-color: rgba(13, 110, 253, 0.08); }
        .transition-transform { transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
        .focus-primary:focus {
          background-color: #fff !important;
          border-color: #0d6efd !important;
          box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.05) !important;
        }
        .shadow-hover:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(13, 110, 253, 0.15) !important;
        }
        .opacity-40 { opacity: 0.4; }
      `}</style>
    </div>
  );
}

export default Feedback;
